// src/pages/AdminAuditoria.js
import React, { useState } from 'react';
import { useAuditoria } from '../hooks/useAuditoria';
import { fechaSegura } from '../utils/formatos';

// Catálogo de acciones con su estilo visual
const ACCIONES = {
    LECTURA:          { label: 'Lectura',          cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    CREACION:         { label: 'Creación',         cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    MODIFICACION:     { label: 'Modificación',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    ELIMINACION:      { label: 'Eliminación',      cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    ACCESO_DENEGADO:  { label: 'Acceso denegado',  cls: 'bg-red-100 text-red-800 border-red-300' },
    AUTENTICACION:    { label: 'Autenticación',    cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    CIERRE_SESION:    { label: 'Cierre de sesión', cls: 'bg-slate-100 text-slate-600 border-slate-200' }
};

const ROLES_LABEL = {
    medico: 'Médico', supermedico: 'Supermédico', recepcion: 'Recepción', admin: 'Administrador'
};

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-all";
const labelCls = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1";

export default function AdminAuditoria() {
    const {
        logs, loading, profesionales,
        pagina, setPagina, meta,
        filtros, setFiltro,
        limpiarFiltros, hayFiltrosActivos
    } = useAuditoria();

    const [detalle, setDetalle] = useState(null); // log seleccionado para ver detalle

    const fechaLarga = (iso) => fechaSegura(iso, "dd MMM yyyy · HH:mm:ss");

    return (
        <div className="min-h-screen p-4 md:p-8 bg-slate-50/50 animate-fade-in">

            {/* CABECERA */}
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                    <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-slate-900 text-white">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                    </span>
                    Registro de Auditoría
                </h1>
                <p className="text-slate-500 font-medium mt-1 ml-1">
                    Trazabilidad legal de accesos y cambios sobre datos de salud (RGPD). Registros inmutables.
                </p>
            </div>

            {/* FILTROS */}
            <div className="bg-white p-4 md:p-5 rounded-3xl shadow-sm border border-slate-200/80 mb-6">
                {/* 🔎 Búsqueda rápida libre (nombre del profesional, recurso o ID) */}
                <div className="relative mb-4">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400 pointer-events-none">
                        {loading ? (
                            <span className="w-4 h-4 border-2 border-clinica-primary border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                        )}
                    </span>
                    <input
                        type="text"
                        value={filtros.busqueda}
                        onChange={(e) => setFiltro('busqueda', e.target.value)}
                        placeholder="Buscar por profesional, acción o recurso… (p. ej. «Canela» o «pacientes»)"
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-3 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-all"
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    <div>
                        <label htmlFor="f-accion" className={labelCls}>Acción</label>
                        <select id="f-accion" value={filtros.accion} onChange={(e) => setFiltro('accion', e.target.value)} className={inputCls}>
                            <option value="">Todas</option>
                            {Object.entries(ACCIONES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="f-usuario" className={labelCls}>Profesional</label>
                        <select id="f-usuario" value={filtros.usuario} onChange={(e) => setFiltro('usuario', e.target.value)} className={inputCls}>
                            <option value="">Todos</option>
                            {profesionales.map((p) => (
                                <option key={p.id || p._id} value={p.id || p._id}>{p.nombre} {p.apellidos}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="f-recurso" className={labelCls}>Recurso</label>
                        <input id="f-recurso" type="text" value={filtros.recurso} onChange={(e) => setFiltro('recurso', e.target.value)} placeholder="Ej. Pacientes, Auth_Login" className={inputCls} />
                    </div>
                    <div>
                        <label htmlFor="f-recursoId" className={labelCls}>ID de registro</label>
                        <input id="f-recursoId" type="text" value={filtros.recursoId} onChange={(e) => setFiltro('recursoId', e.target.value)} placeholder="ID de paciente/consulta" className={inputCls} />
                    </div>
                    <div>
                        <label htmlFor="f-desde" className={labelCls}>Desde</label>
                        <input id="f-desde" type="date" value={filtros.desde} onChange={(e) => setFiltro('desde', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                        <label htmlFor="f-hasta" className={labelCls}>Hasta</label>
                        <input id="f-hasta" type="date" value={filtros.hasta} onChange={(e) => setFiltro('hasta', e.target.value)} className={inputCls} />
                    </div>
                </div>
                {hayFiltrosActivos && (
                    <div className="flex items-center justify-end mt-4">
                        <button onClick={limpiarFiltros} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-all">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            Limpiar filtros
                        </button>
                    </div>
                )}
            </div>

            {/* RESUMEN */}
            <div className="flex items-center justify-between mb-3 px-1">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                    {meta.total} {meta.total === 1 ? 'evento registrado' : 'eventos registrados'}
                </p>
                <p className="text-xs font-bold text-slate-400">Página {pagina} de {meta.totalPages || 1}</p>
            </div>

            {/* TABLA */}
            <div className="bg-white border border-slate-200/80 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[900px]">
                        <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Fecha y hora</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Quién</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Acción</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Recurso</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">Origen (IP)</th>
                            <th className="px-6 py-4 text-[11px] font-black tracking-widest text-slate-400 uppercase text-right">Detalle</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/80">
                        {loading ? (
                            [...Array(6)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-32"></div></td>
                                    <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-9 h-9 bg-slate-100 rounded-xl"></div><div className="h-4 bg-slate-100 rounded w-28"></div></div></td>
                                    <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-full w-24"></div></td>
                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-24"></div></td>
                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-20"></div></td>
                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-10 ml-auto"></div></td>
                                </tr>
                            ))
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-20 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-slate-300">
                                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                                        </div>
                                        <h3 className="text-slate-700 font-black text-lg">Sin eventos</h3>
                                        <p className="text-slate-400 text-sm font-medium">No hay registros que coincidan con los filtros.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            logs.map((log) => {
                                const acc = ACCIONES[log.accion] || { label: log.accion, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                                const nombre = log.actor?.nombre || 'Anónimo';
                                const esAnonimo = !log.usuario && nombre === 'Anónimo';
                                return (
                                    <tr key={log._id || log.id} className="hover:bg-slate-50/80 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-bold text-slate-700 font-mono whitespace-nowrap">{fechaLarga(log.createdAt)}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`flex items-center justify-center w-9 h-9 font-black rounded-xl shrink-0 ${esAnonimo ? 'bg-red-100 text-red-500' : 'bg-slate-900 text-white'}`}>
                                                    {esAnonimo ? '?' : (nombre[0] || 'U')}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-bold text-slate-800 truncate">{nombre}</p>
                                                    <p className="text-[11px] font-semibold text-slate-400 truncate">
                                                        {log.actor?.role ? (ROLES_LABEL[log.actor.role] || log.actor.role) : (log.actor?.email || 'sin identificar')}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-black uppercase tracking-wide border ${acc.cls}`}>
                                                {acc.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-bold text-slate-700">{log.recurso}</p>
                                            {log.recursoId && log.recursoId !== 'N/A' && (
                                                <p className="text-[11px] font-mono text-slate-400 truncate max-w-[160px]">{log.recursoId}</p>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-mono text-slate-500">{log.ip ? String(log.ip).split(',')[0] : '—'}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => setDetalle(log)} className="text-xs font-black text-clinica-primary hover:underline underline-offset-2">
                                                Ver
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINACIÓN */}
                {!loading && logs.length > 0 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                        <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} className="px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 transition-all">Anterior</button>
                        <span className="text-xs font-bold text-slate-400">Página {pagina} de {meta.totalPages || 1}</span>
                        <button disabled={pagina >= (meta.totalPages || 1)} onClick={() => setPagina(pagina + 1)} className="px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 transition-all">Siguiente</button>
                    </div>
                )}
            </div>

            {/* MODAL DE DETALLE */}
            {detalle && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
                    onClick={(e) => { if (e.target === e.currentTarget) setDetalle(null); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setDetalle(null); }}
                    role="button"
                    tabIndex={-1}
                    aria-label="Cerrar detalle"
                >
                    <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                            <div>
                                <h2 className="text-lg font-black text-slate-900 tracking-tight">Detalle del evento</h2>
                                <p className="text-xs font-semibold text-slate-400">{fechaLarga(detalle.createdAt)}</p>
                            </div>
                            <button onClick={() => setDetalle(null)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-4">
                            <DatoDetalle etiqueta="Quién (actor)" valor={`${detalle.actor?.nombre || 'Anónimo'}${detalle.actor?.role ? ' · ' + (ROLES_LABEL[detalle.actor.role] || detalle.actor.role) : ''}`} />
                            {detalle.actor?.email && <DatoDetalle etiqueta="Email" valor={detalle.actor.email} />}
                            {detalle.usuario && <DatoDetalle etiqueta="ID de usuario" valor={detalle.usuario._id || detalle.usuario} mono />}
                            <DatoDetalle etiqueta="Acción" valor={(ACCIONES[detalle.accion]?.label) || detalle.accion} />
                            <DatoDetalle etiqueta="Recurso" valor={detalle.recurso} />
                            {detalle.recursoId && detalle.recursoId !== 'N/A' && <DatoDetalle etiqueta="ID de registro afectado" valor={detalle.recursoId} mono />}
                            <DatoDetalle etiqueta="Petición" valor={`${detalle.metodo || '—'}  ${detalle.ruta || ''}`} mono />
                            <DatoDetalle etiqueta="Código de respuesta" valor={detalle.detalles?.statusCode ?? '—'} />
                            <DatoDetalle etiqueta="Dirección IP" valor={detalle.ip || '—'} mono />
                            {detalle.detalles && (
                                <div>
                                    <p className={labelCls}>Datos de la petición (depurados)</p>
                                    <pre className="bg-slate-900 text-slate-100 text-xs rounded-2xl p-4 overflow-x-auto font-mono leading-relaxed">
{JSON.stringify({ query: detalle.detalles.query, body: detalle.detalles.bodyParams }, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DatoDetalle({ etiqueta, valor, mono }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest w-full sm:w-48 shrink-0">{etiqueta}</span>
            <span className={`text-sm font-bold text-slate-800 break-all ${mono ? 'font-mono text-xs' : ''}`}>{valor}</span>
        </div>
    );
}
